import "./index.scss";
import "../../locales/i18n";

import {useMutation} from "@apollo/client";
import { useFormik } from "formik";
import React, { useState } from "react";
import Scrollbars from "react-custom-scrollbars";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { AccentedDropdown } from "@/components/components/Dropdown";
import Button from "@/components/components/Button";
import { ButtonColor, ButtonSize } from "@/components/components/Button/button.enum";
import { MessageType } from "@/components/enums";
import FormBody from "@/messenger/components/FormBody";
import FormFooter from "@/messenger/components/FormFooter";
import FormHeader from "@/messenger/components/FormHeader";
import TextInput from "@/messenger/components/TextInput";
import { addNotification, removeNotification } from "@/messenger/ducks/general/notifications.duck";
import { updateContact } from "@/messenger/graphql/mutations";
import { TContact } from "@/messenger/types/entities/conversation";
import { editContactValidation, getLocaleLabelFromCode } from "@/messenger/utils/helpers";
/*import { Country, Locale, SuffixOptions } from "@/messenger/constants/misc"*/ 

interface TTextkitEditAContactProps {
    contact: TContact;
    onClose: () => void;
}

const defaultLocaleOptions = [
    {label: "en_US", heading: "English"},
    {label: "es_US", heading: "Spanish"},
    {label: "fr_CA", heading: "French"},
];

const TextKitEditAContact = ({ contact, onClose }: TTextkitEditAContactProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const defaultSelectedLocale = 
    contact.locale 
    ? {label: contact.locale, heading: getLocaleLabelFromCode(contact.locale, false)}
    : {label: "en_US", heading: "English"};

    const [selectedLang, setSelectedLang] = useState(defaultSelectedLocale.label);
    const [advancedOption, setAdvancedOption] = useState<boolean>(false);
    

    const [updateContactMutation] = useMutation(updateContact, {
        onCompleted: () => {
            const notification = {
                id: `${contact.id}_updated`,
                type: MessageType.NEUTRAL,
                message: "Contact updated",
            };

            dispatch(addNotification(notification));
            setTimeout(() => dispatch(removeNotification(`${contact.id}_updated`)), 3000);
            onClose();
        },
    });


    const { values, errors, handleChange, handleSubmit, setFieldValue, dirty, isValid, touched, resetForm, setFieldError } = useFormik({
        initialValues: {
            firstName: contact.firstName ? contact.firstName : "",
            middleName: contact.middleName ? contact.middleName : "",
            lastName: contact.lastName ? contact.lastName : "",
            businessName: contact.businessName ? contact.businessName : "",
            locale: defaultSelectedLocale.heading,
        },
        validationSchema: editContactValidation,
        onSubmit: () => {
            if (isValid) {
                updateContactMutation({
                    variables: {
                        contactId: contact.contactId,
                        firstName: values.firstName,
                        middleName: values.middleName,
                        lastName: values.lastName,
                        businessName: values.businessName,
                        locale: selectedLang,
                    },
                });
            }
        },
    });

    const clearForm = () => {
        resetForm();
        setFieldValue("firstName", contact.firstName ? contact.firstName : "");
        setFieldValue("middleName", contact.middleName ? contact.middleName : "");
        setFieldValue("lastName", contact.lastName ? contact.lastName : "");
        setFieldValue("businessName", contact.businessName ? contact.businessName : "");
        setFieldValue("locale", defaultSelectedLocale.heading);

        setFieldError("firstName", undefined);
        setFieldError("lastName", undefined);
    };

    const handleCancel = () => {
        clearForm();
        onClose();
    };

    const handleSubmitForm = () => {
        handleSubmit();
    };

    /*
    const optionClassNames = {
        optionHeading: "c-first-login__dropdown-option-heading",
    };

    
    const onSuffixSelect = (o: any) => {
        setSuffixSelection(o);
    };

    const suffixOptions = Object.values(SuffixOptions).map(el => {
        return ({
            label: el,
            ariaLabel: el,
            heading: el,
        });
    });
    */

    /*const localeOptions = Locale.map(el => {
        return ({
            label: el?.id,
            ariaLabel: el?.name,
            heading: el?.name,
        });
    });

    const countryOptions = Country.map(el => {
        return ({
            label: el?.id,
            ariaLabel: el?.name,
            heading: el?.name,
        });
    });

    const administrativeAreaOptions = CountryStatesList && CountryStatesList.map((el: { name: string, abbreviation: string, states: [{ name: string, abbreviation: string }] }) => {
        if (el?.abbreviation === countrySelection?.label) {
            return el?.states.map(state => {
                return ({
                    label: state?.abbreviation,
                    ariaLabel: state?.name,
                    heading: state?.name,
                })
            })
        }
    })?.filter(ele => Boolean(ele))[0];*/

    return (
        <div className="c-textkit-edit-a-contact">
            <FormHeader name={"EDIT_CONTACT"} title={"Edit Contact"} onClose={onClose}/>
            <Scrollbars hideTracksWhenNotNeeded={true}>
                <FormBody>
                    <TextInput
                        required
                        name="firstName"
                        label={t("First Name")}
                        inputId={"EDIT_CONTACT_FIRSTNAME"}
                        value={values.firstName}
                        onChange={handleChange}
                        error={(touched.firstName && errors.firstName) ? errors.firstName : undefined}
                    />
                    {advancedOption && <>
                    <div className="c-textkit-edit-a-contact__space"></div>
                    <TextInput 
                        required
                        name="middleName"
                        label={t("Middle Name")}
                        inputId={"EDIT_CONTACT_MIDDLENAME"}
                        value={values.middleName}
                        onChange={handleChange}
                    /></>}
                    <div className="c-textkit-edit-a-contact__space"></div>
                    <TextInput 
                        required
                        name="lastName"
                        label={t("Last Name")}
                        inputId={"EDIT_CONTACT_LASTNAME"}
                        value={values.lastName}
                        onChange={handleChange}
                    />
                    <div className="c-textkit-edit-a-contact__space"></div>
                    {/*advancedOption &&
                    <>
                        <div className="c-text-input__label" id={"SUFFIX"}>
                            {"Suffix"}
                        </div>
                        <SimpleDropdown
                            isMobileView={isMobile}
                            multiple={false}
                            className="c-first-login__dropdown"
                            dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                            heading={suffixSelection ? t(suffixSelection.label) : t("")}
                            onSelect={onSuffixSelect}
                            optionClassNames={optionClassNames}
                            options={suffixOptions}
                            search={false}
                        />
                        <div className="c-add-a-contact__space"></div>
                    </>
                    */}

                {advancedOption &&
                    <>
                        <TextInput
                            inputId={"ADD_CONTACT_BUSINESS_NAME"}
                            name="businessName"
                            label={t("Business Name")}
                            value={values?.businessName}
                            onChange={handleChange}
                            error={errors?.businessName ? errors?.businessName : undefined}
                        />
                        <div className="c-add-a-contact__space"></div>
                    </>
                    }
                    <div className="c-textkit-edit-a-contact__dropdown">
                        <div className="c-textkit-edit-a-contact__dropdown-title" >
                        {t("Customer Language Preference")}
                        </div>
                        
                        <AccentedDropdown 
                            heading={values.locale}
                            options={defaultLocaleOptions}
                            className={"c-textkit-edit-a-contact__dropdown-list"}
                            headerClassName={"c-textkit-edit-a-contact__dropdown-header"}
                            headerTextClassName={"c-textkit-edit-a-contact__dropdown-text"}
                            onSelect={(opt) => {
                                setFieldValue("locale", opt.heading);
                                setSelectedLang(opt.label);
                            }}
                            search={false}
                        />
                        
                    </div>
                    <div className="c-add-a-contact__space"></div>
                    {!advancedOption ? 
                            (<div className="c-form__fotter-button-center">
                                <Button
                                    id={`SHOW_MORE_SUBMIT_BUTTON`}
                                    className="c-form__footer-button"
                                    size={ButtonSize.SMALL}
                                    border={true}
                                    color={ButtonColor.SECONDARY}
                                    onClick={() => setAdvancedOption(true)}
                                    ariaLabel={"See More Options"}
                                >
                                    {"See More Options"}
                                </Button>
                                <div className="c-add-a-contact__space"></div>
                            </div>) : 
                            (<div className="c-form__fotter-button-center">
                                <Button
                                    id={`SHOW_MORE_SUBMIT_BUTTON`}
                                    className="c-form__footer-button"
                                    size={ButtonSize.SMALL}
                                    border={true}
                                    color={ButtonColor.SECONDARY}
                                    onClick={() => setAdvancedOption(false)}
                                    ariaLabel={"Hide Options"}
                                >
                                    {"Hide Options"}
                                </Button>
                                <div className="c-add-a-contact__space"></div>
                            </div>)
                        } 
                </FormBody>
                <FormFooter
                    name={"EDIT_CONTACT"}
                    onCancel={handleCancel} 
                    submitTitle={t("Save")}
                    submitDisabled={!isValid || !dirty}
                    onSubmit={handleSubmitForm} />
            </Scrollbars>
        </div>
    );
};

export default TextKitEditAContact;